import { publicRuntimeValue } from "@matrix-idp/config";
import { Check, KeyRound, LoaderCircle, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { IdentityShell, IdentityStatus } from "../components/IdentityShell.tsx";
import { auth, runOnce } from "../lib/auth-client.ts";

const SCOPE_COPY: Record<string, { title: string; detail: string }> = {
  openid: { title: "确认你的身份", detail: "验证当前登录账号，确认是你本人在授权。" },
  email: { title: "读取邮箱地址", detail: "读取用于登录的邮箱，以便应用识别你。" },
  profile: { title: "读取基本资料", detail: "读取显示名称等基本资料。" },
  offline_access: { title: "保持登录状态", detail: "在你离开后仍可维持已授权会话。" }
};

type PendingDecision = "allow" | "deny" | "switch";

interface AuthorizationDetails {
  client: { id: string; name?: string };
  scope: string;
  user: { email: string };
}

type AuthorizationLoadResult =
  | { kind: "details"; details: AuthorizationDetails }
  | { kind: "redirect"; url: string }
  | { kind: "error"; message: string };

export function Consent() {
  const authorizationId = new URLSearchParams(window.location.search).get("authorization_id");
  const [details, setDetails] = useState<AuthorizationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingDecision | null>(null);
  const scopes = useMemo(() => details?.scope.split(" ").filter(Boolean) ?? [], [details]);
  const deciding = pending !== null;

  useEffect(() => {
    let active = true;
    if (!authorizationId) {
      setError("授权请求无效或已过期。");
      return;
    }
    void runOnce(`oauth-consent:${authorizationId}`, () => loadAuthorization(authorizationId))
      .then((result) => {
        if (!active) return;
        if (result.kind === "redirect") window.location.replace(result.url);
        else if (result.kind === "error") setError(result.message);
        else setDetails(result.details);
      })
      .catch(() => {
        if (active) setError("无法验证当前会话，请重新登录。");
      });
    return () => {
      active = false;
    };
  }, [authorizationId]);

  async function decide(approved: boolean) {
    if (!authorizationId || deciding) return;
    setPending(approved ? "allow" : "deny");
    const result = approved
      ? await auth.approveAuthorization(authorizationId)
      : await auth.denyAuthorization(authorizationId);
    if (result.error || !result.data) {
      setError("无法保存授权决定，请重试。");
      setPending(null);
      return;
    }
    window.location.assign(result.data.redirect_url);
  }

  async function switchAccount() {
    if (!authorizationId || deciding) return;
    setPending("switch");
    const result = await auth.signOut();
    if (result.error) {
      setError("无法切换当前账号，请重试。");
      setPending(null);
      return;
    }
    window.location.replace(loginUrl(authorizationId));
  }

  if (error) {
    return (
      <IdentityStatus
        title="无法完成授权"
        message={error}
        variant="failed"
        step="授权"
        status="需要处理"
        action={{
          label: "返回登录",
          icon: <KeyRound size={17} />,
          onClick: () => window.location.replace("/")
        }}
      />
    );
  }

  if (!details) {
    return (
      <IdentityStatus
        title="正在加载授权请求"
        message="正在确认请求访问的应用…"
        step="授权"
        status="加载中"
      />
    );
  }

  const clientName = details.client.name ?? "已注册应用";

  return (
    <IdentityShell step="授权" status="需要你的确认">
      <main className="auth-main">
        <div className="panel consent-panel">
          <header className="consent-header">
            <p className="eyebrow">应用授权</p>
            <h1>{clientName}</h1>
            <p className="lede">此应用请求访问你的 Matterline 账号。</p>
          </header>

          <div className="panel-body">
            <section className="consent-account" aria-label="当前账号">
              <span className="consent-avatar" aria-hidden="true">
                {accountInitials(details.user.email)}
              </span>
              <div className="consent-account-copy">
                <strong>{details.user.email}</strong>
                <span>当前登录账号</span>
              </div>
              <button
                className="text-action"
                type="button"
                disabled={deciding}
                onClick={() => void switchAccount()}
              >
                {pending === "switch" ? <LoaderCircle className="spin" size={14} /> : null}
                使用其他账号
              </button>
            </section>

            <section className="consent-scopes" aria-label="请求的权限">
              <h2>将获得这些权限</h2>
              <ul>
                {scopes.map((scope) => {
                  const copy = SCOPE_COPY[scope];
                  return (
                    <li key={scope}>
                      <Check size={16} aria-hidden="true" />
                      <div>
                        <strong>{copy?.title ?? scope}</strong>
                        {copy ? <small>{copy.detail}</small> : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            <div className="actions">
              <button
                className="secondary"
                type="button"
                disabled={deciding}
                onClick={() => void decide(false)}
              >
                {pending === "deny" ? <LoaderCircle className="spin" size={17} /> : <X size={17} />}
                拒绝
              </button>
              <button
                className="primary"
                type="button"
                disabled={deciding}
                onClick={() => void decide(true)}
              >
                {pending === "allow" ? (
                  <LoaderCircle className="spin" size={17} />
                ) : (
                  <Check size={17} />
                )}
                允许
              </button>
            </div>
          </div>

          <div className="panel-footer">仅授权你信任的应用。拒绝后不会授予任何访问权限。</div>
        </div>
      </main>
    </IdentityShell>
  );
}

function accountInitials(email: string): string {
  const local = email.split("@")[0] ?? email;
  const parts = local.split(/[._-]+/u).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]!.slice(0, 1)}${parts[1]!.slice(0, 1)}`.toUpperCase();
  }
  return local.slice(0, 2).toUpperCase();
}

function isInvalidAuthSession(error: Error): boolean {
  return (
    error.name === "AuthSessionMissingError" ||
    ("status" in error && (error as Error & { status?: unknown }).status === 401)
  );
}

async function loadAuthorization(authorizationId: string): Promise<AuthorizationLoadResult> {
  const { data: userData, error: userError } = await auth.getUser();
  if (userError) {
    if (isInvalidAuthSession(userError)) {
      await auth.signOut().catch(() => undefined);
      return { kind: "redirect", url: loginUrl(authorizationId) };
    }
    return { kind: "error", message: "无法验证当前会话，请重新登录。" };
  }
  if (!userData.user) {
    await auth.signOut().catch(() => undefined);
    return { kind: "redirect", url: loginUrl(authorizationId) };
  }
  const { data, error: requestError } = await auth.getAuthorizationDetails(authorizationId);
  if (requestError || !data) {
    return { kind: "error", message: "授权请求无效或已过期。" };
  }
  if ("redirect_url" in data) return { kind: "redirect", url: data.redirect_url };
  if (isTuwunelAuthorization(data)) {
    const { data: approval, error: approvalError } =
      await auth.approveAuthorization(authorizationId);
    if (approvalError || !approval) {
      return { kind: "error", message: "无法完成登录授权，请重试。" };
    }
    return { kind: "redirect", url: approval.redirect_url };
  }
  return { kind: "details", details: data };
}

function isTuwunelAuthorization(details: AuthorizationDetails): boolean {
  const clientId = publicRuntimeValue("VITE_TUWUNEL_OAUTH_CLIENT_ID", import.meta.env);
  const scopes = new Set(details.scope.split(" ").filter(Boolean));
  return (
    Boolean(clientId) &&
    details.client.id === clientId &&
    scopes.size === 3 &&
    scopes.has("openid") &&
    scopes.has("profile") &&
    scopes.has("email")
  );
}

function loginUrl(authorizationId: string): string {
  const returnTo = `/oauth/consent?authorization_id=${encodeURIComponent(authorizationId)}`;
  return `/?return_to=${encodeURIComponent(returnTo)}`;
}
